import { Building2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useAuth } from '../AuthContext'
import { Card, CardContent } from './ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'

interface SociedadScopeProps {
  /** Solo se invoca (y solo se monta el contenido) una vez elegida una Sociedad. */
  children: (sociedadId: string) => ReactNode
}

/**
 * Gatea el contenido de los módulos por-Sociedad (Conversaciones, Números de
 * WhatsApp, Base de conocimiento) detrás de un <Select> — el Owner puede
 * tener 10-30 Sociedades, así que un selector es lo único que escala; una
 * fila de chips no. Empieza sin selección a propósito (no hereda la Sociedad
 * "activa" global) para que sea inequívoco de cuál Sociedad son los datos
 * que se están viendo, en vez de asumir la última usada en otro módulo.
 */
export function SociedadScope({ children }: SociedadScopeProps) {
  const { user, setActiveSociedadId } = useAuth()
  const sociedades = user?.sociedades ?? []
  const unica = sociedades.length === 1 ? sociedades[0] : null
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (sociedades.length === 0) return null

  // Con una sola Sociedad no hay nada que elegir — mostrar el selector sería
  // fricción sin propósito.
  if (unica) return <>{children(unica.id)}</>

  function handleChange(id: string): void {
    setSelectedId(id)
    setActiveSociedadId(id)
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Sociedad</span>
        <Select value={selectedId ?? undefined} onValueChange={handleChange}>
          <SelectTrigger className="w-72">
            <SelectValue placeholder="Seleccioná una Sociedad…" />
          </SelectTrigger>
          <SelectContent>
            {sociedades.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedId ? (
        children(selectedId)
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-2 py-20 text-center">
            <Building2 className="size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Seleccioná una Sociedad para continuar.</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
